import { FC } from 'react';
import { WithScope } from '@mews-commander/types';
import { createZodFormResolver, FormCheckboxField, LocalizedText, useDialog, useFormControls } from '@mews/framework';
import { useCommanderSelector } from '@mews-commander/utils';
import { TextKey } from '@mews-commander/enums';
import { FormLayout } from '../../FormLayout';
import {
    getAgeCategoriesCountFormInitialValues,
    getPersonMaxCount,
    getSortedSelectedAgeCategories,
} from '../selectors';
import { AgeCategoriesCounterField } from '../../../../../components/form/hookFields';
import { ExceedMaximumCapacityDialog } from './ExceedMaximumCapacityDialog';
import { isMaxCapacityExceeded } from './utils';
import { ageCategoriesCounterFormSchema, AgeCategoriesCountFormKeys, AgeCategoriesCountFormValues } from './types';
import { useUpdatePersonCountMutation } from './useUpdatePersonCountMutation';

const MAX_PERSON_COUNT = 1000;

interface AgeCategoriesCountFormProps extends WithScope {
    reservationIds: string[];
    serviceId: string;
    onSuccess: () => void;
}

export const AgeCategoriesCountForm: FC<AgeCategoriesCountFormProps> = ({
    scope,
    reservationIds,
    serviceId,
    onSuccess,
}: AgeCategoriesCountFormProps) => {
    const mutation = useUpdatePersonCountMutation(reservationIds);
    const defaultValues = useCommanderSelector((state) =>
        getAgeCategoriesCountFormInitialValues(state, { scope, serviceId })
    );
    const maxCount = useCommanderSelector((state) => getPersonMaxCount(state, { scope }));
    const ageCategories = useCommanderSelector((state) => getSortedSelectedAgeCategories(state, { scope, serviceId }));
    const [{ active, onClose }, openDialog] = useDialog();

    const handleSubmit = (values: AgeCategoriesCountFormValues) => {
        const { ageCategories: ageCategoryValues } = values;
        if (isMaxCapacityExceeded(ageCategoryValues, maxCount)) {
            openDialog();
        } else {
            mutation.mutate(values, { onSuccess: () => onSuccess() });
        }
    };

    const { renderFormContent, watch } = useFormControls<AgeCategoriesCountFormValues>({
        onSubmit: handleSubmit,
        resolver: createZodFormResolver(ageCategoriesCounterFormSchema),
        values: defaultValues,
        dataTestForm: 'age-categories-count-form',
    });

    return (
        <>
            {renderFormContent(
                <FormLayout loading={mutation.isPending}>
                    <AgeCategoriesCounterField
                        name="ageCategories"
                        ageCategories={ageCategories}
                        wrapperProps={{ wrap: false }}
                        size="small"
                        counterWrapperProps={{ grow: true }}
                        maxCapacity={MAX_PERSON_COUNT}
                        required
                    />
                    <FormCheckboxField name={AgeCategoriesCountFormKeys.keepOldPrice}>
                        <LocalizedText textKey={TextKey.KeepOldPrice} />
                    </FormCheckboxField>
                </FormLayout>
            )}
            <ExceedMaximumCapacityDialog
                active={active}
                onClose={onClose}
                onConfirm={() =>
                    mutation.mutate(watch(), {
                        onSuccess: () => {
                            onSuccess();
                            onClose();
                        },
                    })
                }
            />
        </>
    );
};
